import React, { useState, useContext, useEffect, useCallback } from "react";
import {
  makeStyles,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  withStyles,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { AuthContext } from "../context/Auth/AuthContext";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import { i18n } from "../translate/i18n";
import useSocketListener from "../hooks/useSocketListener";
import toastError from "../errors/toastError";
import BackdropLoading from "../components/BackdropLoading";

const backendUrl = getBackendUrl();

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "$ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": { transform: "scale(.8)", opacity: 1 },
    "100%": { transform: "scale(2.4)", opacity: 0 },
  },
}))(Badge);

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.fancyBackground,
    touchAction: "manipulation",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  toolbar: {
    height: 48,
    minHeight: "48px !important",
    paddingLeft: 12,
    paddingRight: 8,
    color: "#ffffff",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: {
    height: 32,
    maxWidth: 120,
    objectFit: "contain",
    content:
      "url(" +
      (theme.mode === "light"
        ? theme.calculatedLogoLight()
        : theme.calculatedLogoDark()) +
      ")",
  },
  content: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    marginTop: 48,
  },
  avatar: {
    width: 32,
    height: 32,
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid rgba(255, 255, 255, 0.3)",
  },
  userMenuWrapper: {
    display: "flex",
    alignItems: "center",
  },
}));

const MobileLayout = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { handleLogout, loading, user, socket } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileUrl, setProfileUrl] = useState(null);

  // Build profile URL
  useEffect(() => {
    const companyId = user?.companyId;
    if (companyId) {
      const savedProfileImage = localStorage.getItem("profileImage");
      const currentProfileImage = savedProfileImage || user.profileImage;
      if (currentProfileImage) {
        setProfileUrl(`${backendUrl}/public/company${companyId}/user/${currentProfileImage}`);
      } else {
        setProfileUrl(`${backendUrl}/public/app/noimage.png`);
      }
    }
  }, [user?.companyId, user?.profileImage]);

  // Socket: user status heartbeat
  useEffect(() => {
    if (socket?.emit && user?.companyId) {
      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket?.emit && socket.emit("userStatus");
      }, 1000 * 60 * 5);
      return () => clearInterval(interval);
    }
  }, [socket, user?.companyId]);

  // Socket: auth event (kicked from another device)
  const handleAuthEvent = useCallback((data) => {
    if (data.user.id === +user?.id) {
      toastError("Sua conta foi acessada em outro computador.");
      setTimeout(() => {
        localStorage.clear();
        window.location.reload();
      }, 1000);
    }
  }, [user?.id]);

  // Socket: profile image update
  const handleUserUpdate = useCallback((data) => {
    if (data.action === "update" && data.user.id === +user?.id) {
      if (data.user.profileImage) {
        const newProfileUrl = `${backendUrl}/public/company${user?.companyId}/user/${data.user.profileImage}`;
        setProfileUrl(newProfileUrl);
        localStorage.setItem("profileImage", data.user.profileImage);
      }
    }
  }, [user?.companyId, user?.id]);

  useSocketListener(socket, user, 'auth', handleAuthEvent);
  useSocketListener(socket, user, 'user', handleUserUpdate);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar variant="dense" className={classes.toolbar}>
          <img className={classes.logo} alt="logo" />

          <div className={classes.userMenuWrapper}>
            <StyledBadge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              variant="dot"
              onClick={handleMenu}
            >
              <Avatar
                alt={user.name || "User"}
                className={classes.avatar}
                src={profileUrl}
              />
            </StyledBadge>

            <Menu
              id="mobile-menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              open={menuOpen}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  minWidth: "160px",
                  borderRadius: "12px",
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
                  marginTop: "8px",
                },
              }}
            >
              <MenuItem
                onClick={handleClickLogout}
                style={{
                  borderRadius: "8px",
                  color: "#ef4444",
                  margin: "4px 8px",
                }}
              >
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>

      <main className={classes.content}>
        {children}
      </main>
    </div>
  );
};

export default MobileLayout;
